import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const InvestmentCalculator = () => {
  const [calculatorType, setCalculatorType] = useState('sip'); // 'sip' or 'lumpsum'
  const [amount, setAmount] = useState(5000);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(12);
  const [result, setResult] = useState({
    totalInvestment: 0,
    totalReturns: 0,
    maturityValue: 0,
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    calculateReturns();
  }, [amount, years, rate, calculatorType]);

  const calculateReturns = () => {
    if (calculatorType === 'sip') {
      const monthlyRate = rate / 12 / 100;
      const months = years * 12;
      const totalInvestment = amount * months;
      
      const maturityValue = amount * 
        ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * 
        (1 + monthlyRate);

      setResult({
        totalInvestment,
        totalReturns: maturityValue - totalInvestment,
        maturityValue,
      });
    } else {
      const totalInvestment = amount;
      const maturityValue = amount * Math.pow(1 + rate / 100, years);

      setResult({
        totalInvestment,
        totalReturns: maturityValue - totalInvestment,
        maturityValue,
      });
    }
  };

  const getChartData = () => {
    const labels = Array.from({ length: years + 1 }, (_, i) => i);
    const investedData = [];
    const wealthData = [];

    labels.forEach((year) => {
      if (calculatorType === 'sip') {
        const monthlyRate = rate / 12 / 100;
        const months = year * 12;
        const invested = amount * months;
        const wealth = amount * 
          ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * 
          (1 + monthlyRate);
        
        investedData.push(invested);
        wealthData.push(wealth);
      } else {
        const invested = amount;
        const wealth = amount * Math.pow(1 + rate / 100, year);
        
        investedData.push(invested);
        wealthData.push(wealth);
      }
    });

    return {
      labels,
      datasets: [
        {
          label: 'Amount Invested',
          data: investedData,
          borderColor: 'rgb(53, 162, 235)',
          backgroundColor: 'rgba(53, 162, 235, 0.5)',
        },
        {
          label: 'Wealth Gained',
          data: wealthData,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: useColorModeValue('gray.800', 'white'),
          padding: 20,
        }
      },
      title: {
        display: true,
        text: 'Investment Growth Over Time',
        color: useColorModeValue('gray.800', 'white'),
        padding: 20,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
        ticks: {
          callback: (value) => '₹' + value.toLocaleString('en-IN'),
          color: useColorModeValue('gray.800', 'white'),
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: useColorModeValue('gray.800', 'white'),
        },
      },
    },
  };

  const SliderWithInput = ({ value, onChange, min, max, step, label }) => (
    <Grid templateColumns="1fr auto" gap={4} alignItems="center" w="full">
      <GridItem>
        <Text mb={2} fontSize="sm" fontWeight="medium">
          {label}
        </Text>
        <Slider
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          step={step}
        >
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb boxSize={6} />
        </Slider>
      </GridItem>
      <GridItem>
        <NumberInput
          value={value}
          onChange={(valueString) => onChange(parseFloat(valueString))}
          min={min}
          max={max}
          step={step}
          w="100px"
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </GridItem>
    </Grid>
  );

  return (
    <Box
      bg={bgColor}
      p={6}
      borderRadius="xl"
      boxShadow="xl"
      border="1px"
      borderColor={borderColor}
    >
      <VStack spacing={6} align="stretch">
        <Heading size="lg" mb={4}>
          Investment Calculator
        </Heading>

        <Tabs isFitted variant="enclosed" onChange={(index) => setCalculatorType(index === 0 ? 'sip' : 'lumpsum')}>
          <TabList mb="1em">
            <Tab>SIP Calculator</Tab>
            <Tab>Lumpsum Calculator</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <Text fontSize="sm" color="gray.600" mb={4}>
                Calculate your returns with Systematic Investment Plan (SIP)
              </Text>
            </TabPanel>
            <TabPanel>
              <Text fontSize="sm" color="gray.600" mb={4}>
                Calculate your returns with one-time investment
              </Text>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <VStack spacing={6}>
          <SliderWithInput
            value={amount}
            onChange={setAmount}
            min={500}
            max={calculatorType === 'sip' ? 100000 : 1000000}
            step={500}
            label={`${calculatorType === 'sip' ? 'Monthly' : 'One-time'} Investment Amount (₹)`}
          />

          <SliderWithInput
            value={years}
            onChange={setYears}
            min={1}
            max={30}
            step={1}
            label="Investment Period (Years)"
          />

          <SliderWithInput
            value={rate}
            onChange={setRate}
            min={1}
            max={30}
            step={0.5}
            label="Expected Return Rate (%)"
          />
        </VStack>

        <Grid 
          templateColumns={{ 
            base: "1fr", 
            md: "repeat(3, 1fr)" 
          }} 
          gap={4} 
          mt={6}
        >
          <Stat>
            <StatLabel>Total Investment</StatLabel>
            <StatNumber fontSize={{ base: "lg", md: "xl" }}>
              ₹{result.totalInvestment.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Total Returns</StatLabel>
            <StatNumber fontSize={{ base: "lg", md: "xl" }}>
              ₹{result.totalReturns.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </StatNumber>
            <StatHelpText>
              {((result.totalReturns / result.totalInvestment) * 100).toFixed(1)}% returns
            </StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Maturity Value</StatLabel>
            <StatNumber fontSize={{ base: "lg", md: "xl" }}>
              ₹{result.maturityValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </StatNumber>
          </Stat>
        </Grid>

        <Box 
          mt={6} 
          p={4} 
          bg={useColorModeValue('white', 'gray.800')} 
          borderRadius="lg"
          border="1px solid"
          borderColor={useColorModeValue('gray.200', 'gray.700')}
          h={{ base: "300px", md: "400px" }}
          position="relative"
          overflow="hidden"
        >
          <Line options={chartOptions} data={getChartData()} />
        </Box>
      </VStack>
    </Box>
  );
};

export default InvestmentCalculator;
